import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { MonitorIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { DeckDialog } from "@/components/dialog/deck";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

import { cn } from "@/lib/utils";
import { requireAuth } from "@/middleware/auth";
import { useDeckListQuery, useDeckQuery } from "@/queries/deck";
import { usePackListQuery, usePackQuery } from "@/queries/pack";

export const Route = createFileRoute("/deck/")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();

	const [packId, setPackId] = useState<string | null>(null);
	const [deckId, setDeckId] = useState<string | null>(null);
	const [search, setSearch] = useState<string>("");
	const [selectedSlideIds, setSelectedSlideIds] = useState<string[]>([]);
	const [selectedSlide, setSelectedSlide] = useState<ISlide | null>(null);
	const [deckDialogOpen, setDeckDialogOpen] = useState(false);

	const packListQueryOptions = usePackListQuery();
	const deckListQueryOptions = useDeckListQuery(packId ?? undefined);
	const packDetailQueryOptions = usePackQuery(packId ?? undefined);
	const deckDetailQueryOptions = useDeckQuery(
		deckId === "new" ? undefined : (deckId ?? undefined),
	);

	const { data: packList } = useQuery(packListQueryOptions);
	const { data: deckList } = useQuery(deckListQueryOptions);
	const { data: packDetail } = useQuery(packDetailQueryOptions);
	const { data: deckDetail } = useQuery(deckDetailQueryOptions);

	const filteredPackList = useMemo(() => {
		return packList?.filter((pack) =>
			pack.name.toLowerCase().includes(search.toLowerCase()),
		);
	}, [packList, search]);

	useEffect(() => {
		if (deckDetail) {
			setSelectedSlideIds(deckDetail.deck.slide_ids);
			setSelectedSlide(deckDetail.slides[0]);
		}
	}, [deckDetail]);

	useEffect(() => {
		if (deckId === "new" && packDetail) {
			setSelectedSlideIds(packDetail?.slides.map((slide) => slide.id) ?? []);
			setSelectedSlide(packDetail?.slides[0] ?? null);
		}
	}, [deckId, packDetail]);

	return (
		<>
			{/* 모바일 전용 화면 */}
			<div className="md:hidden flex items-center justify-center px-4 h-full">
				<div className="text-center space-y-6 max-w-md">
					<div className="rounded-lg space-y-4">
						<MonitorIcon className="mx-auto size-16 text-doxmeet" />
						<h1 className="text-2xl font-bold text-gray-900">
							PC에서 이용해주세요
						</h1>
						<p className="text-gray-600 leading-relaxed">
							내 설명서 기능은 PC 환경에서 더 편리하게 이용할 수 있습니다.
							<br />
							데스크톱 또는 노트북으로 접속해주세요.
						</p>
						<div className="pt-4">
							<Button
								className="w-full bg-doxmeet hover:bg-doxmeet/90"
								onClick={() => router.history.back()}
							>
								이전 페이지로 돌아가기
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* PC 전용 화면 */}
			<div className="hidden md:block px-4 py-4 space-y-2">
				<div className="grid grid-cols-3">
					<div className="flex items-center justify-between pr-3">
						<h3 className="font-bold text-xl whitespace-nowrap">자료선택</h3>
						<input
							placeholder="자료 검색"
							className="ml-auto w-fit mr-3 border-b-2 outline-none border-b-gray-300"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>

				<div className="grid grid-cols-3 gap-4">
					<ScrollArea className="h-[calc(100vh-9rem)]">
						<ul className="grid grid-cols-1 gap-2 pr-3">
							{filteredPackList && filteredPackList.length > 0 ? (
								filteredPackList.map((pack) => (
									<li key={pack.id}>
										<button
											type="button"
											onClick={() => {
												setSelectedSlideIds([]);
												setDeckId(null);
												setSelectedSlide(null);

												if (packId === pack.id) {
													setPackId(null);
													return;
												}

												setPackId(pack.id);
											}}
											className={cn(
												"w-full aspect-video bg-white overflow-hidden cursor-pointer",
												packId === pack.id &&
													"outline-10 -outline-offset-10 outline-doxmeet",
											)}
										>
											<img
												src={pack.thumbnail_url}
												alt={pack.name}
												draggable={false}
												className="size-full object-contain bg-white"
											/>
										</button>
									</li>
								))
							) : (
								<li className="flex items-center justify-center text-center text-muted-foreground py-10">
									검색된 자료가 없습니다.
								</li>
							)}
						</ul>
					</ScrollArea>

					{!!packId && (
						<div className="grid grid-cols-2 gap-4">
							<ScrollArea className="h-[calc(100vh-9rem)]">
								<ul className="grid grid-cols-1 gap-2 pr-3">
									{deckList?.map((deck) => (
										<li key={deck.id}>
											<button
												type="button"
												onClick={() => {
													setSelectedSlideIds([]);
													setSelectedSlide(null);

													if (deckId === deck.id) {
														setDeckId(null);
														return;
													}

													setDeckId(deck.id);
												}}
												className={cn(
													"w-full aspect-video bg-white overflow-hidden cursor-pointer relative",
													deckId === deck.id &&
														"outline-10 -outline-offset-10 outline-doxmeet",
												)}
											>
												<img
													src={deck.pack_thumbnail_url}
													alt={deck.name}
													draggable={false}
													className="size-full object-contain bg-white"
												/>

												<div className="absolute inset-0 flex items-center justify-center bg-black/30">
													<p className="text-white text-lg 2xl:text-2xl font-bold text-shadow-lg">
														{deck.name}
													</p>
												</div>

												<Button
													variant="ghost"
													size="icon"
													className="absolute top-2 right-2 text-white hover:text-red-500"
													onClick={(e) => {
														e.stopPropagation();
													}}
												>
													<Trash2Icon className="size-4" />
												</Button>
											</button>
										</li>
									))}
									<li className="flex items-center justify-center">
										<button
											type="button"
											className="bg-gray-50 text-doxmeet flex w-full items-center justify-center gap-2 aspect-video cursor-pointer"
											onClick={() => {
												setSelectedSlideIds([]);
												setSelectedSlide(null);
												setDeckId("new");
											}}
										>
											<PlusIcon className="size-6 2xl:size-8 2xl:stroke-2" />
											<span className="text-lg 2xl:text-xl text-left font-bold">
												새 설명서 세트
												<br />
												만들기
											</span>
										</button>
									</li>
								</ul>
							</ScrollArea>

							{!!deckId && (
								<ScrollArea className="h-[calc(100vh-9rem)]">
									<ul className="grid grid-cols-1 gap-2 pr-3">
										{packDetail?.slides.map((slide) => (
											<li key={slide.id}>
												<button
													type="button"
													className={cn(
														"relative cursor-pointer select-none w-full aspect-video bg-white overflow-hidden",
														selectedSlide?.id === slide.id &&
															"outline-10 -outline-offset-10 outline-doxmeet",
													)}
													onClick={() => setSelectedSlide(slide)}
												>
													{slide.type === "image" && (
														<img
															src={slide.data}
															alt={slide.id}
															draggable={false}
															className="size-full object-contain bg-white"
														/>
													)}
													{slide.type === "html" && (
														<div
															// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
															dangerouslySetInnerHTML={{
																__html: slide.data,
															}}
															className="aspect-video pointer-events-none"
														/>
													)}
													{!selectedSlideIds.includes(slide.id) && (
														<div className="absolute inset-0 bg-black/70" />
													)}

													<Checkbox
														className="absolute top-2 right-2 size-8"
														iconClassName="size-6"
														checked={selectedSlideIds.includes(slide.id)}
														onClick={(e) => e.stopPropagation()}
														onCheckedChange={() => {
															if (selectedSlideIds.includes(slide.id)) {
																setSelectedSlideIds(
																	selectedSlideIds.filter(
																		(id) => id !== slide.id,
																	),
																);
															} else {
																setSelectedSlideIds([
																	...selectedSlideIds,
																	slide.id,
																]);
															}
														}}
													/>
												</button>
											</li>
										))}
									</ul>
								</ScrollArea>
							)}
						</div>
					)}

					{selectedSlide && (
						<div className="space-y-2">
							{selectedSlide.type === "image" && (
								<img src={selectedSlide.data} alt={selectedSlide.id} />
							)}
							{selectedSlide.type === "html" && (
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
									dangerouslySetInnerHTML={{ __html: selectedSlide.data }}
									className="aspect-video"
								/>
							)}

							<DeckDialog
								open={deckDialogOpen}
								setOpen={setDeckDialogOpen}
								pack_id={packId ?? ""}
								slide_ids={selectedSlideIds}
							/>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
